/*
 * RTA product stories.
 *
 * Concurrency model
 * -----------------
 * All state lives in one custom element instance. Slide-state changes funnel
 * through #apply(), which renders the DOM and then (re)schedules autoplay, so
 * the timer can never disagree with what is on screen.
 *
 * The single pending timeout is invalidated by a generation counter rather
 * than only by clearTimeout: a callback that was already queued when the
 * state changed observes a stale generation and returns without acting. This
 * removes the classic carousel race where a fired-but-not-yet-run timer
 * advances a slide the user just navigated away from.
 *
 * The CMS controls autoplay. Keyboard focus, editor selection, hidden tabs
 * and an offscreen section temporarily pause it. Owner decision 2026-09-30:
 * this slider keeps its fade/blur and CMS autoplay with reduced motion enabled.
 * Other theme animations retain their own preference handling. Pointer movement, hover and
 * pointer-focused controls do not pause or restart the countdown.
 *
 * Images are inert until the first downward scroll, two seconds, or proximity
 * to the media viewport. Proximity activates the remaining images. A requested
 * slide replaces the current one only after loading and decoding; newer input
 * supersedes older decode work without exposing a blank incoming frame.
 *
 * One AbortController owns listeners and image waits. Teardown invalidates
 * navigation, clears both timers and disconnects both observers.
 */

const SWIPE_THRESHOLD_PX = 40;
const AXIS_LOCK_RATIO = 1.2;

class RtaProductStories extends HTMLElement {
  #controller = null;
  #observer = null;
  #imageObserver = null;
  #timer = 0;
  #firstImageTimer = 0;
  #generation = 0;
  #navigationGeneration = 0;
  #index = 0;
  #pendingIndex = null;
  #transient = new Set();
  #slides = [];
  #segments = [];
  #pointer = null;
  #suppressSwipeClickUntil = 0;
  #images = new Map();
  #firstImageActivated = false;
  #allImagesActivated = false;
  #scrollY = 0;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('[data-rta-story-slide]'));
    if (this.#slides.length === 0) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#transient.clear();
    this.#transient.add('image');
    this.#images.clear();
    this.#pendingIndex = null;
    this.#firstImageActivated = false;
    this.#allImagesActivated = false;
    this.#scrollY = window.scrollY;
    if (document.hidden) this.#transient.add('hidden');
    if (this.contains(document.activeElement) && document.activeElement.matches(':focus-visible')) {
      this.#transient.add('focus');
    }

    this.#segments = Array.from(this.querySelectorAll('[data-rta-stories-segment]'));
    this.#index = Math.max(
      0,
      this.#slides.findIndex((slide) => slide.classList.contains('is-current'))
    );

    this.classList.add('rta-stories--enhanced');
    this.#revealControls();

    this.addEventListener('keydown', this.#onKeydown, { signal });
    // A pointer click can focus a button too; only keyboard-visible focus
    // pauses rotation. Mouse movement and hover have no timer listeners.
    this.addEventListener('focusin', (event) => {
      this.#setTransient('focus', event.target.matches(':focus-visible'));
    }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.#setTransient('focus', false);
    }, { signal });

    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    document.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary) this.#pointer = null;
    }, { signal });
    document.addEventListener('pointermove', this.#onPointerMove, { passive: true, signal });
    document.addEventListener('pointerup', this.#onPointerUp, { signal });
    document.addEventListener('pointercancel', () => { this.#pointer = null; }, { signal });
    this.addEventListener('click', (event) => {
      if (event.detail && performance.now() < this.#suppressSwipeClickUntil &&
        event.target.closest('[data-rta-stories-viewport]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { capture: true, signal });

    document.addEventListener('visibilitychange', this.#onVisibilityChange, { signal });

    this.querySelector('[data-rta-stories-previous]')?.addEventListener(
      'click', () => this.#navigate(-1, { announce: true }), { signal }
    );
    this.querySelector('[data-rta-stories-next]')?.addEventListener(
      'click', () => this.#navigate(1, { announce: true }), { signal }
    );
    this.#segments.forEach((segment) => {
      segment.addEventListener('click', () => {
        const target = Number(segment.dataset.index);
        if (Number.isInteger(target)) this.#goTo(target, { announce: true });
      }, { signal });
    });

    // The Theme Editor selects a block: show it and hold still while editing.
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });

    if ('IntersectionObserver' in window) {
      this.#transient.add('offscreen');
      this.#observer = new IntersectionObserver(
        (entries) => {
          if (signal.aborted) return;
          for (const entry of entries) this.#setTransient('offscreen', !entry.isIntersecting);
        },
        { threshold: 0.5 }
      );
      this.#observer.observe(this);

      this.#imageObserver = new IntersectionObserver(
        (entries) => {
          if (!signal.aborted && entries.some((entry) => entry.isIntersecting)) this.#activateAllImages();
        },
        { rootMargin: '400px 0px', threshold: 0 }
      );
      this.#imageObserver.observe(this.querySelector('[data-rta-stories-viewport]') ?? this);
    } else {
      window.addEventListener('resize', this.#checkImageApproach, { passive: true, signal });
      this.#checkImageApproach();
    }

    window.addEventListener('scroll', this.#onPageScroll, { passive: true, signal });
    if (!this.#firstImageActivated) {
      this.#firstImageTimer = window.setTimeout(() => {
        if (!signal.aborted) this.#activateFirstImage();
      }, 2000);
    }
    this.#apply();
    // Reconnecting an editor-moved section keeps its already activated image.
    if (this.#slides[this.#index].querySelector('img')) this.#activateFirstImage();
  }

  disconnectedCallback() {
    this.#clearTimer();
    this.#navigationGeneration += 1;
    window.clearTimeout(this.#firstImageTimer);
    this.#firstImageTimer = 0;
    this.#controller?.abort();
    this.#controller = null;
    this.#observer?.disconnect();
    this.#observer = null;
    this.#imageObserver?.disconnect();
    this.#imageObserver = null;
    this.#images.clear();
    this.#pendingIndex = null;
    this.#pointer = null;
    this.#suppressSwipeClickUntil = 0;
  }

  #revealControls() {
    if (this.#slides.length < 2) return;
    for (const node of this.querySelectorAll(
      '[data-rta-stories-previous], [data-rta-stories-next], [data-rta-stories-controls]'
    )) {
      node.hidden = false;
    }
  }

  get #loops() {
    return this.dataset.loop === 'true';
  }

  get #interval() {
    const parsed = Number(this.dataset.interval);
    return Number.isFinite(parsed) && parsed >= 5000 ? Math.min(parsed, 30000) : 10000;
  }

  get #canAutoplay() {
    if (this.dataset.autoplay !== 'true') return false;
    if (this.#slides.length < 2) return false;
    if (this.#transient.size > 0) return false;
    if (!this.#loops && this.#index >= this.#slides.length - 1) return false;
    return true;
  }

  #setTransient(reason, active) {
    const had = this.#transient.has(reason);
    if (active === had) return;
    if (active) this.#transient.add(reason);
    else this.#transient.delete(reason);
    this.#apply();
  }

  #onVisibilityChange = () => {
    this.#setTransient('hidden', document.visibilityState === 'hidden');
  };

  #onBlockSelect = (event) => {
    const slide = event.target?.closest?.('[data-rta-story-slide]');
    if (!slide || !this.contains(slide)) return;
    this.#setTransient('editor', true);
    this.#goTo(Number(slide.dataset.index) || 0, { announce: false });
  };

  #onBlockDeselect = (event) => {
    if (!this.contains(event.target)) return;
    this.#setTransient('editor', false);
  };

  #onKeydown = (event) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
    this.#setTransient('focus', true);
    switch (event.key) {
      case 'ArrowLeft':
        this.#navigate(-1, { announce: true });
        break;
      case 'ArrowRight':
        this.#navigate(1, { announce: true });
        break;
      case 'Home':
        this.#goTo(0, { announce: true });
        break;
      case 'End':
        this.#goTo(this.#slides.length - 1, { announce: true });
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  #onPointerDown = (event) => {
    this.#setTransient('focus', false);
    if (!event.isPrimary) {
      this.#pointer = null;
      return;
    }
    // A fresh mouse click or finger tap must not inherit a prior swipe lock.
    this.#suppressSwipeClickUntil = 0;
    if (event.pointerType === 'mouse' || !event.target.closest('[data-rta-stories-viewport]')) return;
    this.#pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, cancelled: false };
  };

  #onPointerMove = (event) => {
    const start = this.#pointer;
    if (!start || start.id !== event.pointerId) return;
    const dx = Math.abs(event.clientX - start.x);
    const dy = Math.abs(event.clientY - start.y);
    if (dy >= SWIPE_THRESHOLD_PX && dy > dx * AXIS_LOCK_RATIO) start.cancelled = true;
  };

  #onPointerUp = (event) => {
    const start = this.#pointer;
    this.#pointer = null;
    if (!start || start.id !== event.pointerId || start.cancelled) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    // A vertical gesture is a page scroll, never a slide change.
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy) * AXIS_LOCK_RATIO) return;

    // The same gesture may produce a compatibility click on a full-width
    // advance zone. Consume it rather than advancing a second time.
    this.#suppressSwipeClickUntil = performance.now() + 700;
    this.#navigate(dx < 0 ? 1 : -1, { announce: true });
  };

  #navigate(direction, options) {
    const last = this.#slides.length - 1;
    let next = (this.#pendingIndex ?? this.#index) + direction;
    if (next > last) next = this.#loops ? 0 : last;
    if (next < 0) next = this.#loops ? last : 0;
    this.#goTo(next, options);
  }

  #goTo(index, options = {}) {
    if (!Number.isInteger(index) || !this.#controller) return;
    const clamped = Math.min(Math.max(index, 0), this.#slides.length - 1);
    const generation = ++this.#navigationGeneration;
    const { signal } = this.#controller;
    this.#firstImageActivated = true;
    window.clearTimeout(this.#firstImageTimer);
    this.#firstImageTimer = 0;
    this.#pendingIndex = clamped;
    this.#transient.add('image');
    this.#apply();

    this.#loadSlideImage(clamped).then((ready) => {
      if (signal.aborted || !this.isConnected || generation !== this.#navigationGeneration) return;
      this.#pendingIndex = null;
      this.#transient.delete('image');
      const changed = clamped !== this.#index;
      if (ready) this.#index = clamped;
      this.#apply(ready && changed ? options : {});
    });
  }

  #onPageScroll = () => {
    const currentY = window.scrollY;
    if (currentY > this.#scrollY) this.#activateFirstImage();
    this.#scrollY = currentY;
    if (!('IntersectionObserver' in window)) this.#checkImageApproach();
  };

  #checkImageApproach = () => {
    const viewport = this.querySelector('[data-rta-stories-viewport]') ?? this;
    const rect = viewport.getBoundingClientRect();
    if (rect.top <= window.innerHeight + 400 && rect.bottom >= -400) this.#activateAllImages();
  };

  #activateFirstImage() {
    if (this.#firstImageActivated || !this.#controller) return;
    this.#goTo(this.#index);
  }

  #activateAllImages() {
    if (this.#allImagesActivated || !this.#controller) return;
    this.#allImagesActivated = true;
    this.#activateFirstImage();
    this.#slides.forEach((slide, index) => this.#loadSlideImage(index));
    this.#imageObserver?.disconnect();
    this.#imageObserver = null;
  }

  #loadSlideImage(index) {
    const slide = this.#slides[index];
    if (this.#images.has(slide)) return this.#images.get(slide);
    const { signal } = this.#controller;
    const template = slide.querySelector('template[data-rta-story-image]');
    if (template) {
      const content = template.content.cloneNode(true);
      // Once explicitly activated, native lazy thresholds must not postpone
      // the requested image or leave an offscreen decode wait unresolved.
      content.querySelectorAll('img').forEach((image) => { image.loading = 'eager'; });
      template.replaceWith(content);
    }
    const image = slide.querySelector('img');
    const pending = image ? this.#imageReady(image, signal) : Promise.resolve(true);
    this.#images.set(slide, pending);
    pending.then(() => {
      // Recheck readiness on later visits: a responsive source may have changed.
      if (this.#images.get(slide) === pending) this.#images.delete(slide);
    });
    return pending;
  }

  async #imageReady(image, signal) {
    const loaded = await new Promise((resolve) => {
      if (signal.aborted) return resolve(false);
      if (image.complete) return resolve(image.naturalWidth > 0);
      const finish = (ready) => {
        image.removeEventListener('load', onLoad);
        image.removeEventListener('error', onError);
        signal.removeEventListener('abort', onAbort);
        resolve(ready);
      };
      const onLoad = () => finish(true);
      const onError = () => finish(false);
      const onAbort = () => finish(false);
      image.addEventListener('load', onLoad, { once: true });
      image.addEventListener('error', onError, { once: true });
      signal.addEventListener('abort', onAbort, { once: true });
    });
    if (!loaded || signal.aborted) return false;
    try {
      if (typeof image.decode === 'function') await image.decode();
    } catch {
      return false;
    }
    return !signal.aborted && image.complete && image.naturalWidth > 0;
  }

  /*
   * The one place that renders state. Rendering first and scheduling last
   * guarantees a queued timer always reflects what is actually shown.
   */
  #apply(options = {}) {
    this.#clearTimer();

    this.#slides.forEach((slide, position) => {
      const isCurrent = position === this.#index;
      slide.classList.toggle('is-current', isCurrent);
      slide.toggleAttribute('inert', !isCurrent);
      slide.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
    });

    this.#segments.forEach((segment, position) => {
      const isCurrent = position === this.#index;
      segment.classList.toggle('is-current', isCurrent);
      if (isCurrent) segment.setAttribute('aria-current', 'true');
      else segment.removeAttribute('aria-current');
    });

    this.classList.toggle('rta-stories--paused', !this.#canAutoplay);

    if (options.announce) this.#announce();
    if (this.#canAutoplay) this.#schedule();
  }

  #announce() {
    const live = this.querySelector('[data-rta-stories-live]');
    const current = this.#slides[this.#index];
    if (!live || !current) return;
    live.textContent = current.getAttribute('aria-label') ?? '';
  }

  #schedule() {
    const generation = ++this.#generation;
    this.#timer = window.setTimeout(() => {
      // Stale callback from a superseded state, or a detached element.
      if (generation !== this.#generation || !this.isConnected) return;
      if (!this.#canAutoplay) return;
      this.#navigate(1, { announce: false });
    }, this.#interval);
  }

  #clearTimer() {
    this.#generation += 1;
    if (this.#timer) {
      window.clearTimeout(this.#timer);
      this.#timer = 0;
    }
  }
}

if (!customElements.get('rta-product-stories')) {
  customElements.define('rta-product-stories', RtaProductStories);
}
